import { useState, useEffect } from "react";
import { useLang } from "@/context/LanguageContext";

const heroImages = [
  { url: "https://customer-assets.emergentagent.com/job_elite-sports-hub-7/artifacts/v415rbe3_Freeride.jpg", position: "center center" },
  { url: "https://customer-assets.emergentagent.com/job_elite-sports-hub-7/artifacts/s41meey0_Freeride.webp", position: "center center" },
  { url: "https://customer-assets.emergentagent.com/job_elite-sports-hub-7/artifacts/eo7hsjy3_b00301a7-91a0-42cd-b62e-cc455f52dc18.JPG", position: "center center" },
];

export default function Hero() {
  const { t } = useLang();
  const [currentImg, setCurrentImg] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImg((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [setCurrentImg]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative h-screen flex items-center justify-start overflow-hidden"
    >
      {/* Background Images with crossfade */}
      {heroImages.map((img, i) => (
        <div
          key={img.url}
          className="absolute inset-0 bg-cover transition-opacity duration-1000"
          style={{
            backgroundImage: `url('${img.url}')`,
            backgroundPosition: img.position,
            opacity: i === currentImg ? 1 : 0,
          }}
        />
      ))}
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="max-w-3xl">
          {/* Coming Soon Badge */}
          <div
            className="inline-block mb-6 animate-fade-in"
            style={{ animationDelay: "0.15s" }}
          >
            <span
              data-testid="hero-coming-soon-badge"
              className="inline-block bg-[#FF3B30] text-white font-heading text-xs sm:text-sm uppercase tracking-[0.25em] px-5 py-2"
            >
              {t.hero.badge}
            </span>
          </div>

          {/* Accent label */}
          <div
            className="flex items-center gap-3 mb-6 animate-fade-in"
            style={{ animationDelay: "0.3s" }}
          >
            <div className="w-12 h-[2px] bg-[#FF3B30]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] font-body">
              La Nucia, Spain
            </span>
          </div>

          {/* Main Title */}
          <h1
            className="font-heading text-5xl sm:text-6xl lg:text-8xl uppercase leading-none tracking-tight text-white animate-fade-in"
            style={{ animationDelay: "0.5s" }}
          >
            {t.hero.title1}
            <br />
            <span className="text-[#FF3B30]">{t.hero.title2}</span>
          </h1>

          {/* Tagline */}
          <p
            className="mt-6 text-sm sm:text-base lg:text-lg text-[#A3A3A3] font-body leading-relaxed max-w-xl animate-fade-in"
            style={{ animationDelay: "0.7s" }}
          >
            {t.hero.tagline}
          </p>

          {/* CTAs */}
          <div
            className="mt-10 flex flex-wrap gap-4 animate-fade-in"
            style={{ animationDelay: "0.9s" }}
          >
            <button
              data-testid="hero-apply-btn"
              onClick={() => scrollTo("forms")}
              className="bg-[#FF3B30] text-white font-heading uppercase tracking-widest px-8 py-4 text-sm hover:bg-[#FF5C53] transition-all hover:-translate-y-0.5"
            >
              {t.hero.cta}
            </button>
            <button
              data-testid="hero-learn-btn"
              onClick={() => scrollTo("about")}
              className="border border-[#262626] text-white font-heading uppercase tracking-widest px-8 py-4 text-sm hover:border-[#FF3B30] hover:text-[#FF3B30] transition-all hover:-translate-y-0.5"
            >
              {t.hero.ctaSecondary}
            </button>
          </div>
        </div>
      </div>

      {/* Image dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroImages.map((img, i) => (
          <button
            key={`dot-${i}`}
            onClick={() => setCurrentImg(i)}
            className={`w-2 h-2 rounded-full transition-all ${i === currentImg ? "bg-[#FF3B30] w-6" : "bg-white/30"}`}
            data-testid={`hero-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
}
